import { ConfigStateService } from '@abp/ng.core';
import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { getTenantUserNameStorageKey } from '../designer/utils';
import { BrandingSettingsService } from '../../settings/branding-settings/proxy/branding-settings.service';
import { BrandingSettingsDto } from '../../settings/branding-settings/proxy/branding-settings.model';
import { filterXSS } from 'xss';

type Disclaimer = { header: string; content: { title: string; descriptions: string[] }[] };

@Component({
  selector: 'cai-ai-usage-disclaimer',
  standalone: false,
  templateUrl: './ai-usage-disclaimer.component.html',
  styleUrl: './ai-usage-disclaimer.component.scss',
})
export class AIUsageDisclaimerComponent implements OnInit, OnDestroy {
  private _visible = false;

  get visible() {
    return this._visible;
  }
  @Input() set visible(visible: boolean) {
    if (!this._visible && visible) {
      this.showSwitch = false;
    }

    this._visible = visible;
  }
  @Output() visibleChange = new EventEmitter<boolean>();

  showSwitch = true;
  storageKey = 'aiUsageDisclaimerDoNotShowAgain';
  defaultLanguage = 'en';
  disclaimers: Record<string, Disclaimer> = {
    en: {
      header: 'Artificial Intelligence Usage Disclaimer',
      content: [
        {
          title: 'This system is powered by artificial intelligence',
          descriptions: [
            `When you use SESTEK AI Agents, AI models process conversations data to deliver features such as intent detection, speech-to-text transcription, sentiment analysis and more.`,
            `<br />`,
            `We commit to ensuring transparency, accountability, and human oversight in the use of the AI technologies, while also safeguarding privacy and data security. No fully automated decision-making processes are conducted without appropriate safeguards.`,
          ],
        },
        {
          title: 'AI Outputs and Actions',
          descriptions: [
            `AI outputs are generated based on user-defined inputs and configurations. While AI systems are designed for accuracy, its not guaranteed as they are not infallible. Always double-check the outputs before making critical decisions.`,
          ],
        },
        {
          title: 'More information',
          descriptions: [
            `For detailed information regarding AI usage, personal data processing, and our compliance standards, review our <a href='https://www.sestek.com/privacy-policy' target="_blank">Privacy Notice</a>, <a href='https://www.sestek.com/sestek-artificial-intelligence-usage-and-responsibility-guide' target="_blank">SESTEK AI Usage and Responsibility Guide</a> and <a href='https://www.sestek.com/sestek-ai-solutions-privacy-notice' target="_blank">SESTEK AI Solutions Privacy Notice</a>.`,
            `If you have any questions or concerns please contact us at: <a href='mailto:info@sestek.com'>info@sestek.com</a>`,
          ],
        },
      ],
    },
    tr: {
      header: 'Yapay Zekâ Kullanım Bildirimi',
      content: [
        {
          title: 'Bu sistem, yapay zekâ ile desteklenmektedir',
          descriptions: [
            `SESTEK AI Agents'ı kullandığınızda, yapay zeka modelleri niyet tespiti, konuşmadan metne dönüştürme, duygu analizi ve daha fazlası gibi özellikleri sunmak için konuşma verilerini işler.`,
            '<br />',
            `Yapay zekâ teknolojilerinin kullanımında şeffaflık, hesap verebilirlik ve insan denetimini sağlamayı; aynı zamanda gizlilik ve veri güvenliğini korumayı taahhüt ederiz. Uygun güvenlik önlemleri olmadan tamamen otomatik karar verme süreçleri yürütülmez.`,
          ],
        },
        {
          title: 'Yapay Zekâ Çıktıları ve Sorumluluk',
          descriptions: [
            `Yapay zekâ çıktıları, kullanıcı tarafından belirlenen girdiler ve yapılandırmalar doğrultusunda üretilir. Yapay zekâ sistemleri yüksek doğruluk için tasarlanmış olsa da yanılmaz değildir; kritik kararlar almadan önce çıktıları mutlaka kontrol edin.`,
          ],
        },
        {
          title: 'Daha Fazla Bilgi',
          descriptions: [
            `Yapay zekâ kullanımı, kişisel veri işleme ve uyum süreçlerimize dair detaylı bilgi için <a href='https://www.sestek.com/tr/kvkk-politikasi' target="_blank">KVKK Politikası</a>, <a href='https://www.sestek.com/tr/sestek-yapay-zeka-kullanim-ve-sorumluluk-kilavuzu' target="_blank">SESTEK Yapay Zekâ Kullanım ve Sorumluluk Kılavuzu</a> ve <a href='https://www.sestek.com/tr/sestek-yapay-zeka-cozumleri-aydinlatma-metni' target="_blank">SESTEK Yapay Zekâ Çözümleri Aydınlatma Metni</a> içeriklerini inceleyebilirsiniz. `,
            `Herhangi bir soru veya endişeniz olursa lütfen bizimle iletişime geçin: <a href='mailto:info@sestek.com'>info@sestek.com</a>`,
          ],
        },
      ],
    },
    ar: {
      header: 'إخلاء مسؤولية استخدام الذكاء الاصطناعي',
      content: [
        {
          title: 'هذا النظام مدعوم بالذكاء الاصطناعي',
          descriptions: [
            `عند استخدام SESTEK AI Agents، تقوم نماذج الذكاء الاصطناعي بمعالجة بيانات المحادثات لتقديم ميزات مثل اكتشاف النية، وتحويل الكلام إلى نص، وتحليل المشاعر والمزيد.`,
            '<br />',
            `نلتزم بضمان الشفافية والمساءلة والرقابة البشرية في استخدام تقنيات الذكاء الاصطناعي، مع الحفاظ على خصوصية وأمن البيانات. ولا تُتخذ أي قرارات مؤتمتة بالكامل من دون وجود الضوابط والضمانات المناسبة.`,
          ],
        },
        {
          title: 'نتائج وإجراءات الذكاء الاصطناعي',
          descriptions: [
            `يتم إنشاء جميع نتائج الذكاء الاصطناعي بناءً على المعطيات المدخلة والإعدادات المحددة من قبل المستخدم. وعلى الرغم من أن أنظمة الذكاء الاصطناعي مصممة لتكون دقيقة، إلا أن دقتها غير مضمونة وقد تشتمل النتائج على أخطاء. يُرجى التحقق من النتائج دائمًا قبل اتخاذ قرارات حاسمة`,
          ],
        },
        {
          title: 'مزيد من المعلومات',
          descriptions: [
            `للحصول على معلومات مفصلة حول استخدام الذكاء الاصطناعي ومعالجة البيانات الشخصية ومعايير الامتثال الخاصة بنا، يرجى مراجعة إشعار الخصوصية <a href='https://www.sestek.com/privacy-policy' target="_blank">Privacy Notice</a> ودليل استخدام ومسؤولية الذكاء الاصطناعي <a href='https://www.sestek.com/sestek-artificial-intelligence-usage-and-responsibility-guide' target="_blank">SESTEK AI Usage and Responsibility Guide</a> وإشعار الخصوصية لحلول الذكاء الاصطناعي <a href='https://www.sestek.com/sestek-ai-solutions-privacy-notice' target="_blank">SESTEK AI Solutions Privacy Notice</a>.`,
            `للاستفسارات أو الملاحظات، يُرجى التواصل عبر: <a href='mailto:info@sestek.com'>info@sestek.com</a>`,
          ],
        },
      ],
    },
  };

  disclaimer: Disclaimer = this.disclaimers[this.defaultLanguage];
  form: FormGroup;
  customDisclaimerText: string | null = null;
  sanitizedHtml: string | null = null;
  isLoading = true;

  constructor(
    private config: ConfigStateService,
    private fb: FormBuilder,
    private brandingSettingsService: BrandingSettingsService,
  ) {
    const userLang =
      this.config.getOne('localization')?.currentCulture.cultureName || this.defaultLanguage;
    this.disclaimer = this.disclaimers[userLang] || this.disclaimers[this.defaultLanguage];

    this.form = this.fb.group({ doNotShowAgain: [false] });
  }

  ngOnInit() {
    this.loadCustomDisclaimer();

    const cacheKey = getTenantUserNameStorageKey(this.config, this.storageKey);
    const doNotShowAgainStr = localStorage.getItem(cacheKey);
    const doNotShowAgain = doNotShowAgainStr === 'true';

    this.form.patchValue({ doNotShowAgain }, { emitEvent: false });
    this.form.markAsPristine();
    this.form.markAsUntouched();

    this.form.get('doNotShowAgain').valueChanges.subscribe((value) => {
      const cacheKey = getTenantUserNameStorageKey(this.config, this.storageKey);

      localStorage.setItem(cacheKey, value.toString());
      this.form.markAsPristine();
      this.form.markAsUntouched();
    });

    if (!doNotShowAgain && cacheKey != this.storageKey) {
      this._visible = true;
      this.visibleChange.emit(this.visible);
    }

    // Listen for branding updates
    window.addEventListener('branding-updated', this.handleBrandingUpdate.bind(this));
  }

  ngOnDestroy(): void {
    window.removeEventListener('branding-updated', this.handleBrandingUpdate.bind(this));
  }

  private loadCustomDisclaimer(): void {
    this.brandingSettingsService.get().subscribe({
      next: (settings: BrandingSettingsDto) => {
        if (settings.aiUsageDisclaimer) {
          this.customDisclaimerText = settings.aiUsageDisclaimer;
          this.sanitizedHtml = this.sanitizeContent(settings.aiUsageDisclaimer);
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading AI usage disclaimer:', err);
        this.isLoading = false;
      },
    });
  }

  private handleBrandingUpdate(event: Event): void {
    const customEvent = event as CustomEvent<BrandingSettingsDto>;
    if (customEvent.detail?.aiUsageDisclaimer) {
      this.customDisclaimerText = customEvent.detail.aiUsageDisclaimer;
      this.sanitizedHtml = this.sanitizeContent(customEvent.detail.aiUsageDisclaimer);
    }
  }

  /**
   * Sanitize markdown content to prevent XSS attacks
   * Uses xss library to filter potentially malicious content
   */
  private sanitizeContent(markdown: string): string {
    // XSS filter with whitelist of safe tags and attributes
    const options = {
      whiteList: {
        h1: [],
        h2: [],
        h3: [],
        h4: [],
        h5: [],
        h6: [],
        p: [],
        br: [],
        strong: [],
        em: [],
        u: [],
        ul: [],
        ol: [],
        li: [],
        a: ['href', 'title', 'target'],
        code: ['class'],
        pre: ['class'],
        blockquote: [],
      },
      stripIgnoreTag: true, // Remove tags not in whitelist
      stripIgnoreTagBody: ['script', 'style'], // Remove content of these tags
    };

    return filterXSS(markdown, options);
  }

  closeModal() {
    this._visible = false;
    this.visibleChange.emit(this.visible);
  }
}
